// Design templates: workspace-scoped, reusable looks that many screens
// reference. The list is small (a handful per workspace), so a plain
// DataTable without pagination matches the locations page.

import {
    Button,
    DataTable,
    type DataTableColumn,
    DestructiveConfirm,
    PageHead,
    SearchBar,
    toast,
} from "@pej/kore/components";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Copy, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { ApiError } from "@/api/error";
import { useCreateTemplate, useDeleteTemplate, useTemplates } from "@/api/templates";
import type { Template } from "@/api/types";
import { toastMutationError } from "@/components/common/toast-mutation-error";
import { pinnedActionsTable, rowActionsColumn } from "@/components/row-actions";
import { filterRows } from "@/features/search/matches-search-rq";
import { duplicateName, toTemplateBody } from "@/features/templates/duplicate";
import { TEMPLATES_SEARCH_MANIFEST } from "@/features/templates/templates-filters";

export const Route = createFileRoute("/w/$workspaceId/templates/")({
    component: TemplatesPage,
});

function TemplatesPage() {
    const { workspaceId } = Route.useParams();
    const navigate = useNavigate();
    const templatesQuery = useTemplates(workspaceId);
    const createTemplate = useCreateTemplate(workspaceId);
    const deleteTemplate = useDeleteTemplate(workspaceId);
    const [deleteTarget, setDeleteTarget] = useState<Template | null>(null);
    const [search, setSearch] = useState("");

    // Templates are tens of rows per workspace at most, so this list stays a
    // plain GET filtered in the browser — see
    // src/features/search/matches-search-rq.ts for why that's safe.
    const rows = filterRows(templatesQuery.data ?? [], { query: { value: search } }, (t) => [t.name]);

    const columns: DataTableColumn<Template>[] = [
        {
            key: "name",
            header: "Name",
            cell: (template) => (
                <span className="flex items-center gap-2">
                    <span
                        aria-hidden="true"
                        className="inline-block size-4 shrink-0 rounded-sm border border-border"
                        style={{ backgroundColor: template.backgroundColor }}
                    />
                    <span className="font-medium">{template.name}</span>
                </span>
            ),
        },
        rowActionsColumn(
            (template) => `Actions for ${template.name}`,
            (template) => [
                {
                    id: "duplicate",
                    label: "Duplicate",
                    icon: Copy,
                    disabled: createTemplate.isPending,
                    onSelect: () => void onDuplicate(template),
                },
                {
                    id: "delete",
                    label: "Delete",
                    icon: Trash2,
                    destructive: true,
                    onSelect: () => setDeleteTarget(template),
                },
            ],
        ),
    ];

    // A screen's whole look and copy live on its template, so "this screen
    // needs different wording" means a template of its own — duplicating an
    // existing one is the short path to that.
    async function onDuplicate(template: Template) {
        const taken = (templatesQuery.data ?? []).map((t) => t.name);
        try {
            const created = await createTemplate.mutateAsync({
                ...toTemplateBody(template),
                name: duplicateName(template.name, taken),
            });
            toast.success(`Duplicated as ${created.name}`);
            void navigate({
                to: "/w/$workspaceId/templates/$templateId",
                params: { workspaceId, templateId: created.id },
            });
        } catch (err) {
            if (err instanceof ApiError) {
                toastMutationError("Duplicate failed", err);
                return;
            }
            throw err;
        }
    }

    async function confirmDelete() {
        if (!deleteTarget) return;
        try {
            await deleteTemplate.mutateAsync(deleteTarget.id);
            toast.success("Template deleted");
        } catch (err) {
            if (err instanceof ApiError) {
                // Screens still using it: the toast names or counts them and
                // links there.
                toastMutationError("Delete failed", err, { workspaceId });
            } else {
                throw err;
            }
        } finally {
            setDeleteTarget(null);
        }
    }

    return (
        <div className="flex h-full flex-col gap-6">
            {/* h-full, so the table below fills the viewport instead of
                growing the page: `main` has a definite height (flex-1 inside
                the h-screen, overflow-hidden shell), so an h-full child fits
                exactly and no outer scrollbar appears. The table takes the
                remaining height and scrolls its own rows — see the
                `min-h-0 flex-1` on it, and kore's DataTable root, which is
                flex-col precisely so a height-constrained container pins its
                pager to the bottom. Same shape as the vouchers admin's
                crud-page. */}
            <PageHead
                size="h1"
                title="Templates"
                description="The shared look and wording screens reference — colors, logo, font, copy, CSS."
                action={
                    <Button onClick={() => navigate({ to: "/w/$workspaceId/templates/new", params: { workspaceId } })}>
                        <Plus aria-hidden="true" />
                        Create template
                    </Button>
                }
            />
            {/* kore's own SearchBar, the same control the locations and screens
                pages render — one search box shape across the app, by being
                the same component rather than by matching its classes.
                `useKoreSearch` is deliberately skipped: that hook exists to
                back filters with the URL, and a query-only manifest has no
                filters to back, so a stubbed router adapter would be wiring
                that reads as load-bearing and is not. `filters={[]}` cannot
                change (no facet is declared, so no trigger can render), which
                is why `onFiltersChange` has nothing to do.

                SearchBar debounces `onQueryChange` at 200ms. For a
                browser-side filter over tens of rows that is a slight delay
                before the list narrows — the right trade for one consistent
                control, and not worth a bypass. */}
            <SearchBar
                manifest={TEMPLATES_SEARCH_MANIFEST}
                filters={[]}
                onFiltersChange={() => undefined}
                query={{ value: search, searchBy: null }}
                onQueryChange={(next) => setSearch(next.value)}
                // See the note on the other two lists: compact is the house
                // pair, and kore defaults to the roomier one.
                size="compact"
            />
            {/* No Card around it: kore's DataTable already renders a
                bordered, rounded surface, and wrapping it in one stacked two
                borders with a hairline above and below the rows — the same
                reasoning screens.index.tsx records. The Card that used to be
                here also sat in the middle of the flex chain the full-height
                layout needs. */}
            <DataTable
                className={`min-h-0 flex-1 ${pinnedActionsTable}`}
                density="compact"
                caption="Templates"
                columns={columns}
                rows={rows}
                rowKey={(template) => template.id}
                state={templatesQuery.isLoading ? "loading" : undefined}
                empty={
                    search.trim()
                        ? {
                              headline: `No templates match "${search.trim()}"`,
                              body: "Try a different search.",
                          }
                        : {
                              headline: "No templates yet",
                              body: "Create a template to give screens a shared look.",
                          }
                }
                onRowClick={(template) =>
                    void navigate({
                        to: "/w/$workspaceId/templates/$templateId",
                        params: { workspaceId, templateId: template.id },
                    })
                }
            />
            <DestructiveConfirm
                open={deleteTarget !== null}
                onOpenChange={(open) => {
                    if (!open) setDeleteTarget(null);
                }}
                title="Delete template"
                description={
                    <>
                        This permanently deletes <strong>{deleteTarget?.name}</strong>. Deletion is refused while any
                        screen still references it.
                    </>
                }
                expectedConfirmation={deleteTarget?.name ?? ""}
                echoLabel="Type the template's name to confirm"
                primaryLabel="Delete"
                confirming={deleteTemplate.isPending}
                onConfirm={confirmDelete}
            />
        </div>
    );
}
